'use client';

import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { analytics } from 'libs/segment';
import { Optional } from 'utility-types';
import type { AnalyticsProperties, TrackingFunction } from './types';
import { useAnalyticsModuleTrackingAttributes } from './AnalyticsModuleTracking';
import { getAdditionalTrackingProperties, getCookieValue } from './utils';
import { ANON_ID_COOKIE } from 'utils/cookies';
import { ANALYTICS_EVENTS } from './types';

const getAnonymousId = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return getCookieValue(ANON_ID_COOKIE) || null;
};

type SegmentPageOptions = {
  integrations?: Optional<Record<string, boolean>>;
  autoTrack?: boolean;
};

export default function SegmentPage(props: SegmentPageOptions) {
  useSegmentPage(props);
  return null;
}

/**
 * A custom hook that handles page view tracking in Segment while disabling specific analytics integrations.
 * This hook automatically tracks page views when the pathname or search params change.
 *
 * @param {Object} options - Configuration options for page tracking
 * @param {Object} [options.integrations] - Optional integrations configuration to override defaults
 * @param {boolean} [options.autoTrack=true] - Whether to automatically track page views on route changes
 * @returns {Function} A function that can be called to manually trigger page tracking
 *
 * @example
 * ```tsx
 * function Page() {
 *   useSegmentPage({
 *     autoTrack: true,
 *     integrations: { 'Custom Integration': false }
 *   });
 *   return <div>Page Content</div>;
 * }
 * ```
 */
export function useSegmentPage({ integrations = {}, autoTrack = true }: SegmentPageOptions): TrackingFunction {
  const [trackedAlready, setTrackedAlready] = useTrackedAlready(false);
  const pathname = usePathname();

  const trackPage = useCallback(() => {
    const customOptions = {
      integrations,
    };

    // Always fire standard page tracking with explicit path
    analytics.page(
      {
        path: pathname,
        url: window.location.href,
      },
      customOptions,
    );
  }, [integrations, pathname]);

  useEffect(() => {
    if (autoTrack && !trackedAlready) {
      analytics.track(ANALYTICS_EVENTS.VISITOR_ID_SET, {
        source: 'self-generated',
        anonymous_id: getAnonymousId(),
      });

      trackPage();
      setTrackedAlready(true);
    }
  }, [autoTrack, trackPage, trackedAlready, setTrackedAlready]);

  return trackPage;
}

/**
 * A hook that triggers Segment tracking events with customized integration settings.
 * Can be configured to track automatically on route changes or manually via the returned function.
 *
 * @param {Object} props - The tracking event configuration
 * @param {string} props.eventName - The name of the event to track
 * @param {Object} props.properties - Properties/data to include with the tracking event
 * @param {boolean} [autoTrack=true] - Whether to automatically trigger tracking on route changes
 * @returns {Function} A function that can be called to manually trigger the tracking event
 *
 * @example
 * ```tsx
 * function CheckoutButton() {
 *   const trackPurchase = useSegmentTrack({
 *     eventName: 'Purchase Completed',
 *     properties: { total: 99.99, currency: 'USD' }
 *   });
 *
 *   return <button onClick={trackPurchase}>Complete Purchase</button>;
 * }
 * ```
 */
export function useSegmentTrack<T extends AnalyticsProperties>(
  props: { eventName: string; properties: T; opts?: { shouldSendToFacebook: boolean } },
  autoTrack: boolean = false,
): TrackingFunction {
  const moduleAttributes = useAnalyticsModuleTrackingAttributes();
  const [trackedAlready, setTrackedAlready] = useTrackedAlready(false);

  const baseIntegrations = useMemo(
    () =>
      props.opts?.shouldSendToFacebook
        ? {}
        : {
            integrations: {
              'Facebook Pixel': false,
              'Facebook Conversions API': false,
              'Facebook Offline Conversions': false,
            },
          },
    [props.opts?.shouldSendToFacebook],
  );

  const trackEvent = useCallback(
    (eventProperties: Record<string, unknown> = {}, callback?: () => void) => {
      if (!analytics || !props.eventName) return;

      const properties = {
        ...props.properties,
        ...eventProperties,
        additional_data: [
          ...moduleAttributes,
          ...('additional_data' in props.properties && Array.isArray(props.properties.additional_data)
            ? props.properties.additional_data
            : []),
          ...(eventProperties && 'additional_data' in eventProperties && Array.isArray(eventProperties.additional_data)
            ? eventProperties.additional_data
            : []),
        ],
        ...getAdditionalTrackingProperties(),
      };

      analytics.track(props.eventName, properties, baseIntegrations, callback);
    },
    [props.eventName, props.properties, moduleAttributes, baseIntegrations],
  );

  useEffect(() => {
    if (autoTrack && !trackedAlready) {
      trackEvent();
      setTrackedAlready(true);
    }
  }, [autoTrack, trackEvent, trackedAlready, setTrackedAlready]);

  return trackEvent;
}

function useTrackedAlready(initialValue: boolean): [boolean, Dispatch<SetStateAction<boolean>>] {
  const prevPathname = useRef('');
  const prevSearchParams = useRef('');
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [trackedAlready, setTrackedAlready] = useState(initialValue);

  useEffect(() => {
    // If the pathname or search params have changed, track the page view. This prevents URI fragments from triggering a re-track.
    if (prevPathname?.current !== pathname || prevSearchParams?.current !== searchParams?.toString()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets tracking state on route change
      setTrackedAlready(false);
    }
    prevPathname.current = pathname;
    prevSearchParams.current = searchParams?.toString();
  }, [pathname, searchParams]);

  return [trackedAlready, setTrackedAlready];
}
